import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

interface AuthorizeInfo {
    app: {
        name: string;
        description: string | null;
        website_url: string | null;
        icon_url: string | null;
        is_approved: boolean;
    };
    scopes: { id: string; description: string }[];
}

/**
 * OAuth 2.0 consent screen. Third-party apps send users here with
 * ?client_id=&redirect_uri=&scope=&state=&code_challenge=&code_challenge_method=S256.
 * The server re-validates everything; this page only renders and forwards.
 */
export default function OAuthAuthorizePage() {
    const { user, isLoading } = useAuth();
    const [params] = useSearchParams();
    const [info, setInfo] = useState<AuthorizeInfo | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const clientId = params.get('client_id') ?? '';
    const redirectUri = params.get('redirect_uri') ?? '';
    const scope = params.get('scope') ?? '';
    const state = params.get('state') ?? '';
    const codeChallenge = params.get('code_challenge') ?? '';
    const codeChallengeMethod = params.get('code_challenge_method') ?? '';

    const paramsOk =
        clientId && redirectUri && scope && state && codeChallenge && codeChallengeMethod === 'S256';

    useEffect(() => {
        if (!user || !paramsOk) return;
        api.get<{ data: AuthorizeInfo }>(
            `/oauth/authorize-info?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}`
        )
            .then((res) => setInfo(res.data))
            .catch((e) =>
                setError(e instanceof ApiError ? e.message : 'Could not load application details.')
            );
    }, [user, paramsOk, clientId, redirectUri, scope]);

    const approve = async () => {
        setSubmitting(true);
        try {
            const res = await api.post<{ redirect_to: string }>('/oauth/authorize', {
                client_id: clientId,
                redirect_uri: redirectUri,
                scope,
                state,
                code_challenge: codeChallenge,
                code_challenge_method: codeChallengeMethod,
            });
            window.location.href = res.redirect_to;
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Authorization failed.');
            setSubmitting(false);
        }
    };

    const deny = () => {
        // redirect_uri was validated server-side via authorize-info before we
        // got here; still, only redirect after a successful info load.
        if (info) {
            const sep = redirectUri.includes('?') ? '&' : '?';
            window.location.href = `${redirectUri}${sep}error=access_denied&state=${encodeURIComponent(state)}`;
        } else {
            window.location.href = '/dashboard';
        }
    };

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-950">
                <p className="text-sm text-zinc-500">Loading...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-950 px-5 sm:px-6">
                <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-center">
                    <h1 className="text-lg font-semibold text-zinc-100">Sign in required</h1>
                    <p className="mt-2 text-sm text-zinc-400">
                        Log in to DebtBound, then restart the connection from the application you
                        are linking.
                    </p>
                    <Link
                        to="/login"
                        className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                    >
                        Go to login
                    </Link>
                </div>
            </div>
        );
    }

    if (!paramsOk || error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-950 px-5 sm:px-6">
                <div className="w-full max-w-md rounded-xl border border-red-900/50 bg-zinc-900 p-6 text-center">
                    <h1 className="text-lg font-semibold text-zinc-100">Authorization error</h1>
                    <p className="mt-2 text-sm text-zinc-400">
                        {error ?? 'This authorization link is malformed or incomplete.'}
                    </p>
                    <Link
                        to="/dashboard"
                        className="mt-4 inline-block rounded-lg bg-zinc-700 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-600"
                    >
                        Back to dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (!info) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-950">
                <p className="text-sm text-zinc-500">Loading application details...</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-950 px-5 sm:px-6">
            <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 p-6">
                <div className="flex items-center gap-3">
                    {info.app.icon_url ? (
                        <img
                            src={info.app.icon_url}
                            alt=""
                            className="h-12 w-12 rounded-lg object-cover"
                        />
                    ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600/20 text-lg font-bold text-indigo-400">
                            {info.app.name.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <h1 className="text-lg font-semibold text-zinc-100">{info.app.name}</h1>
                        <p className="text-xs text-zinc-500">wants to access your account</p>
                    </div>
                </div>

                {!info.app.is_approved && (
                    <p className="mt-4 rounded-lg border border-amber-900/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-300">
                        This app is unverified and running in developer test mode. Only its owner
                        can authorize it.
                    </p>
                )}

                {info.app.description && (
                    <p className="mt-4 text-sm text-zinc-400">{info.app.description}</p>
                )}

                <div className="mt-5">
                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                        This will allow {info.app.name} to:
                    </p>
                    <ul className="mt-2 space-y-2">
                        {info.scopes.map((s) => (
                            <li key={s.id} className="flex items-start gap-2 text-sm text-zinc-300">
                                <span className="mt-0.5 text-emerald-400">✓</span>
                                <span>
                                    {s.description}
                                    <span className="ml-1 font-mono text-xs text-zinc-600">
                                        ({s.id})
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="mt-4 text-xs text-zinc-500">
                    You will be redirected to{' '}
                    <span className="break-all font-mono text-zinc-400">{redirectUri}</span>. You
                    can revoke access at any time from Settings → Connected apps.
                </p>

                <div className="mt-6 flex gap-3">
                    <button
                        onClick={deny}
                        disabled={submitting}
                        className="flex-1 rounded-lg border border-zinc-700 px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
                    >
                        Deny
                    </button>
                    <button
                        onClick={approve}
                        disabled={submitting}
                        className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                    >
                        {submitting ? 'Authorizing…' : 'Authorize'}
                    </button>
                </div>
            </div>
        </div>
    );
}
